import * as B from '@blueprintjs/core';
import {useMutation} from '@tanstack/react-query';
import React from 'react';

import AgentPromptInput from 'app/components/AgentExplorer/AgentPromptInput';
import {api} from 'app/modules/Remote';
import {ApiResponseError} from 'app/utils/apiUtils';

import {ManageFeaturesActions, NumberedFeatureCollection} from './ManageFeaturesProvider';

interface Props {
  setUploadedFeatureCollection: ManageFeaturesActions['setUploadedFeatureCollection'];
  onGenerateStart: (prompt: string) => void;
}

const ManagePropertiesAgentPrompt: React.FC<Props> = ({
  setUploadedFeatureCollection,
  onGenerateStart,
}) => {
  const [promptText, setPromptText] = React.useState('');
  // A generation that returns nothing is an outcome rather than a failure, so it's tracked
  // separately from the mutation's error state.
  const [noResults, setNoResults] = React.useState(false);

  const generatePortfolio = useMutation({
    mutationFn: (prompt: string) => api.agent.generatePortfolio(prompt),
    onMutate: (prompt) => {
      setNoResults(false);
      onGenerateStart(prompt);
    },
    onSuccess: (response) => {
      const {featureCollection} = response.toJS().data as {
        featureCollection: NumberedFeatureCollection;
      };
      if (!featureCollection.features.length) {
        setNoResults(true);
        return;
      }
      setUploadedFeatureCollection({
        ...featureCollection,
        features: featureCollection.features.map((feature, index) => ({
          ...feature,
          id: index + 1,
        })),
      });
    },
  });

  return (
    <>
      <AgentPromptInput
        value={promptText}
        onChange={setPromptText}
        onSubmit={generatePortfolio.mutate}
        loading={generatePortfolio.isPending}
        loadingMessage="This will take a few minutes"
        height="16rem"
        placeholder="Describe the properties you want to find, e.g. sites from recent cheatgrass papers where early bloom and browning should be visible…"
      />
      {generatePortfolio.isError && (
        <B.Callout intent={B.Intent.DANGER}>
          {(generatePortfolio.error as unknown as ApiResponseError)?.error?.message ||
            'Failed to generate features. Please try again.'}
        </B.Callout>
      )}
      {noResults && (
        <B.Callout intent={B.Intent.WARNING}>
          The agent didn&apos;t find any properties for that prompt. Try naming a region, or
          describing the kind of site you want to monitor.
        </B.Callout>
      )}
    </>
  );
};

export default ManagePropertiesAgentPrompt;
